import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["student", "teacher", "admin"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function AdminUsers() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status === "guest") navigate("/login");
    else if (status === "authed" && user?.role !== "admin") navigate("/");
  }, [status, user, navigate]);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "admin") return;
    api.get("/users")
      .then(({ data }) => setUsers(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [status, user]);

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, role } : x)));
      toast.success(`${u.name} is now ${role}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? Their journal entries stay published, but they will no longer be able to sign in.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success("Account removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (status !== "authed" || user?.role !== "admin") {
    return (
      <div className="min-h-screen" data-testid="admin-users-denied">
        <Navbar />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-users-page">
      <Navbar />
      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-5xl px-6 pb-32 pt-32"
      >
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-primary">
          <ShieldCheck className="h-4 w-4" /> Administrator
        </p>
        <h1 className="mt-4 font-serif text-5xl font-semibold tracking-tighter" data-testid="admin-users-heading">
          Manage the journal's keepers.
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Promote trusted students to teacher or admin, or remove accounts. Removing an account keeps its published entries in the journal.
        </p>

        <div className="mt-14 border border-border bg-card" data-testid="users-table">
          <div className="hidden grid-cols-12 gap-4 border-b border-border px-6 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:grid">
            <span className="col-span-4">Member</span>
            <span className="col-span-2">Joined</span>
            <span className="col-span-2">Entries</span>
            <span className="col-span-2">Role</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {loading ? (
            <div className="animate-pulse space-y-4 p-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 bg-secondary" />
              ))}
            </div>
          ) : (
            users.map((u) => {
              const isSelf = u.user_id === user.user_id;
              return (
                <div
                  key={u.user_id}
                  data-testid={`user-row-${u.user_id}`}
                  className="grid grid-cols-1 items-center gap-4 border-b border-border px-6 py-5 last:border-b-0 md:grid-cols-12"
                >
                  <div className="col-span-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-secondary font-serif text-lg font-semibold text-primary">
                      {u.name?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                    <div>
                      <p className="text-sm font-bold" data-testid={`user-name-${u.user_id}`}>
                        {u.name} {isSelf && <span className="text-xs font-normal text-primary">(you)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">{u.email} · {u.provider}</p>
                    </div>
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">{fmtDate(u.created_at)}</p>
                  <p className="col-span-2 text-xs text-muted-foreground" data-testid={`user-entries-${u.user_id}`}>
                    {u.entries} {u.entries === 1 ? "entry" : "entries"} · {u.comments} notes
                  </p>
                  <div className="col-span-2">
                    <select
                      value={u.role}
                      disabled={isSelf}
                      onChange={(e) => changeRole(u, e.target.value)}
                      data-testid={`role-select-${u.user_id}`}
                      className="w-full border border-border bg-background px-3 py-2 text-xs font-bold uppercase tracking-widest outline-none transition-colors focus:border-primary disabled:opacity-50"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <button
                      onClick={() => removeUser(u)}
                      disabled={isSelf}
                      data-testid={`delete-user-${u.user_id}`}
                      className="flex items-center gap-2 border border-destructive px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-destructive transition-colors duration-300 hover:bg-destructive hover:text-destructive-foreground disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </motion.main>
    </div>
  );
}
